import { Button, useDisclosure } from "@heroui/react";
import { useEffect, useState, useCallback } from "react";
import { api } from "@/api";
import { Lock, LockPasscode } from "@/api/locks";
import { toast } from "react-toastify";
import { TableBuilder } from "@/app/components/tables/tableBuilder.tsx";
import { FaPencil, FaPlus } from "react-icons/fa6";
import { CreatePasscodeModal } from "@/app/components/modals/lock/createPasscodeModal.tsx";
import { EditPasscodeModal } from "@/app/components/modals/lock/editPasscodeModal.tsx";
import useErrorHandling from "@/hooks/useErrorHandling";
import { ColumnType } from "@/app/components/tables/tableTypes.ts";
import { useAppSelector } from "@/hooks/useAppSelector";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { setSelectedLock } from "@/app/store/locks/reducer";
import { NewLockHandbook } from "@/app/components/handbooks/lockHandbook";
import { skudLinks, Links } from "@/app/components/navigation/links/Links";
import { useLocation } from "react-router-dom";

export function SkudPasscodesPage() {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const selectedLockId = useAppSelector((state) => state.locks.selectedLockId);

  const [lock, setLock] = useState<Lock | null>(null);
  const [passcodes, setPasscodes] = useState<LockPasscode[] | null>(null);
  const [passcode, setPasscode] = useState<LockPasscode | null>(null);

  const handleError = useErrorHandling();

  const createPasscodeModalDisclosure = useDisclosure();
  const editPasscodeModalDisclosure = useDisclosure();

  const fullscreenDisclosure = useDisclosure();

  const editPasscode = (passcode: LockPasscode) => {
    setPasscode(passcode);
    editPasscodeModalDisclosure.onOpen();
  };

  const handleUnlock = useCallback(() => {
    if (!selectedLockId) {
      toast.error("Выберите замок");
      return;
    }

    api.locks
      .unlock(selectedLockId)
      .then(() => toast.success("Замок успешно открыт!"))
      .catch((err) => {
        console.log(err);
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      });
  }, [selectedLockId, handleError]);

  useEffect(() => {
    if (!selectedLockId) {
      setLock(null);
      setPasscodes(null);
      return;
    }

    api.locks
      .get(selectedLockId)
      .then((response) => {
        setLock(response.data);
      })
      .catch((err) => {
        console.log(err);
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      });

    api.locks
      .listPasscodes(selectedLockId)
      .then((response) => {
        setPasscodes(response.data);
      })
      .catch((err) => {
        console.log(err);
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      });
  }, [
    selectedLockId,
    createPasscodeModalDisclosure.isOpen,
    editPasscodeModalDisclosure.isOpen,
    handleError,
  ]);

  return (
    <>
      <CreatePasscodeModal
        disclosure={createPasscodeModalDisclosure}
        lock={lock}
      />
      <EditPasscodeModal
        disclosure={editPasscodeModalDisclosure}
        lock={lock}
        passcode={passcode}
      />
      <div className="flex flex-col md:flex-row gap-4 w-full">
        <div className="flex flex-col gap-2 md:min-w-[240px] w-full md:w-auto">
          {/* @ts-ignore */}
          <NewLockHandbook
            errorMessage=""
            isInvalid={false}
            onSelectionChange={(key: any) =>
              dispatch(setSelectedLock(Number(key)))
            }
            selectedKey={selectedLockId ? String(selectedLockId) : undefined}
          />
          <Links
            links={skudLinks(location.pathname, !!selectedLockId, handleUnlock)}
          />
        </div>
        <div className="flex-grow flex flex-col gap-2">
          <div className="w-full flex flex-col gap-4 bg-white dark:bg-zinc-900 p-4 rounded-xl border-2 border-foreground-200 dark:border-foreground-100">
            {!selectedLockId && (
              <div className="flex flex-col items-center justify-center p-8">
                <span className="text-foreground-500">
                  Выберите замок для управления паролями
                </span>
              </div>
            )}
            {selectedLockId && (
              <>
                <div className="flex flex-row w-full items-center">
                  <div className="flex flex-row w-full gap-4 items-center">
                    <div className="flex flex-row items-center gap-2">
                      <span className="font-medium">
                        Замок {lock?.display_name}
                      </span>
                    </div>
                    <Button
                      startContent={<FaPlus />}
                      size="sm"
                      color="primary"
                      variant="solid"
                      className="max-w-fit"
                      onClick={() => createPasscodeModalDisclosure.onOpen()}>
                      Создать пароль
                    </Button>
                  </div>
                </div>
                <TableBuilder
                  fullscreenDisclosure={fullscreenDisclosure}
                  removeWrapper
                  displayOptions={false}
                  columns={[
                    {
                      label: "Пароль",
                      key: "passcode",
                      type: ColumnType.String,
                    },
                    {
                      key: "actions",
                      label: "Действия",
                      type: ColumnType.Actions,
                      actions: [
                        {
                          icon: <FaPencil />,
                          onClick: (_value, row) => {
                            editPasscode(row);
                          },
                        },
                      ],
                    },
                  ]}
                  data={passcodes || []}
                  rowsPerPage={10}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
